import { type FC, useContext } from 'react'

import { InputLabel } from '../inputLabel'
import { OptionLabel } from '../optionLabel'

import { Radio } from './Radio'
import { RadioGroupContext } from './RadioGroup'
import type { RadioGroupOptionProps } from './types'

/**
 * RadioGroup.Option component for individual radio options.
 *
 * Must be used within a RadioGroup component.
 * Automatically wraps children in OptionLabel with appropriate size.
 *
 * @example
 * Simple usage with text:
 * ```tsx
 * <RadioGroup value={selected} onChange={setSelected}>
 *   <RadioGroup.Option value="option1">Option 1</RadioGroup.Option>
 *   <RadioGroup.Option value="option2">Option 2</RadioGroup.Option>
 * </RadioGroup>
 * ```
 *
 * @example
 * With complex content:
 * ```tsx
 * <RadioGroup value={selected} onChange={setSelected}>
 *   <RadioGroup.Option value="option1">
 *     <div>
 *       <div className="font-semibold">Title</div>
 *       <div className="text-sm">Description</div>
 *     </div>
 *   </RadioGroup.Option>
 * </RadioGroup>
 * ```
 */
export const RadioGroupOption: FC<RadioGroupOptionProps> = ({
  value,
  children,
  disabled: optionDisabled,
  ...props
}) => {
  const context = useContext(RadioGroupContext)

  if (!context) {
    throw new Error('RadioGroup.Option must be used within a RadioGroup component')
  }

  const { value: groupValue, onChange, size, disabled: groupDisabled, error, name } = context

  const isChecked = groupValue === value
  const isDisabled = groupDisabled || optionDisabled

  const handleChange = () => {
    if (!isDisabled) {
      onChange(value)
    }
  }

  return (
    <InputLabel disabled={isDisabled}>
      <Radio
        {...props}
        name={name}
        value={value}
        checked={isChecked}
        onChange={handleChange}
        disabled={isDisabled}
        error={error}
        size={size}
      />
      <OptionLabel size={size}>{children}</OptionLabel>
    </InputLabel>
  )
}

RadioGroupOption.displayName = 'RadioGroupOption'
